import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Search, Filter, Star, Package } from 'lucide-react';
import BackButton from '../components/ui/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadInitial();
  }, []);

  const loadInitial = async () => {
    try {
      const [catRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/shop/categories`),
        fetch(`${API_URL}/api/shop/settings`),
      ]);
      const [cats, sett] = await Promise.all([catRes.json(), settingsRes.json()]);
      setCategories(cats);
      setSettings(sett);
    } catch (e) {
      console.error(e);
    }
    loadProducts();
  };

  const loadProducts = async (search = '', category = '') => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      const res = await fetch(`${API_URL}/api/shop/products?${params}`);
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts(searchQuery, selectedCategory);
  };

  const handleCategoryClick = (catId) => {
    const next = catId === selectedCategory ? '' : catId;
    setSelectedCategory(next);
    loadProducts(searchQuery, next);
  };

  const sym = settings.currency_symbol || 'CA$';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <BackButton />
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-pink-500" />
              <h1 className="text-xl font-bold text-gray-900">{settings.shop_name || 'Shop'}</h1>
            </div>
            <Link to="/shop/cart" className="relative" data-testid="cart-link">
              <ShoppingBag className="w-6 h-6 text-gray-600" />
            </Link>
          </div>
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="shop-search-input"
              />
            </div>
            <button type="submit" className="bg-pink-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-pink-600 transition-colors" data-testid="shop-search-btn">
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
            <button
              onClick={() => handleCategoryClick('')}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === '' ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-pink-300'
              }`}
              data-testid="category-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.id ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-pink-300'
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Products Grid */}
        {searching ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No products found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/shop/product/${product.id}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square bg-gray-100 overflow-hidden">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">{product.name}</h3>
                  {product.stock !== undefined && product.stock <= 0 && (
                    <span className="text-xs text-red-500 font-medium">Out of stock</span>
                  )}
                  <p className="text-pink-600 font-bold mt-1">
                    {sym}{parseFloat(product.price).toFixed(2)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
